import { CacheService } from '.';
import * as Bixby from './../core';

export abstract class StaticDataService {
  private static adminBaseUrl = Bixby.Constants.API_ADMIN_PREFIX;
  private static readonly staticDataTimeoutMinutes: number = 7 * 24 * 60;

  // Client/Admin
  public static async getNewsFilters(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(Bixby.Constants.API_NEWSTYPECONTROLLER);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_NEWS_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  // Admin
  public static async getAccountingTypes(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${this.adminBaseUrl}/${Bixby.Constants.API_ACCOUNTINGTYPECONTROLLER}`
    );
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_ACCOUNTING_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getCompanyReportingTypes(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${this.adminBaseUrl}/${Bixby.Constants.API_COMPANYREPORTINGTYPECONTROLLER}`
    );
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_COMPANY_REPORTING_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getCompanyTransactionTypes(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${this.adminBaseUrl}/${Bixby.Constants.API_COMPANYTRANSACTIONTYPECONTROLLER}`
    );
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_COMPANY_TRANSACTION_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getCurrencies(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_CURRENCYCONTROLLER}`);
    CacheService.remove(Bixby.Constants.CACHE_STATIC_CURRENCIES_REFRESHED);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_CURRENCIES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getDataSiteHosts(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_DATASITEHOSTCONTROLLER}`);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_DATA_SITE_HOSTS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getDataSiteProviders(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_DATASITEPROVIDERCONTROLLER}`);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_DATA_SITE_PROVIDERS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getDataSiteStatus(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${this.adminBaseUrl}/${Bixby.Constants.API_DATASITESTATUSCONTROLLER}`
    );
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_DATA_SITE_STATUS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getDataSiteTypes(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_DATASITETYPECONTROLLER}`);
    CacheService.remove(Bixby.Constants.CACHE_STATIC_DATA_SITE_TYPES_REFRESHED);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_DATA_SITE_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getDebtIssuanceTypes(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(
      `${this.adminBaseUrl}/${Bixby.Constants.API_DEBTISSUANCETYPECONTROLLER}`
    );
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_DEBT_ISSUANCE_TYPES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getIndustries(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(Bixby.Constants.API_INDUSTRYCONTROLLER);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_INDUSTRIES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getIndustryGroups(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(Bixby.Constants.API_INDUSTRYGROUPCONTROLLER);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_INDUSTRY_GROUPS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getSectors(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_SECTORCONTROLLER}`);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_SECTORS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getSubscriptions(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(`${this.adminBaseUrl}/${Bixby.Constants.API_SUBSCRIPTIONCONTROLLER}`);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_SUBSCRIPTIONS,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }

  public static async getUserTitles(): Promise<Bixby.Models.IIdentifiable[]> {
    const route = Bixby.API.ServerRoute.forAction(Bixby.Constants.API_USERTITLECONTROLLER);
    return await CacheService.getOrSetAsync<Bixby.Models.IIdentifiable[]>(
      Bixby.Constants.CACHE_STATIC_USER_TITLES,
      () => Bixby.API.get(route),
      StaticDataService.staticDataTimeoutMinutes
    );
  }
}
